import React, { useState } from "react";
import { multiFactor, TotpMultiFactorGenerator } from "firebase/auth";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff } from "lucide-react";
import { fbAuth, firebaseErrorMessage } from "@/lib/firebase";

export default function MfaSettings() {
  const fu = fbAuth.currentUser;
  const [factors, setFactors] = useState(() => (fu ? multiFactor(fu).enrolledFactors : []));
  const [secret, setSecret] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (!fu) {
    return <p className="p-6 text-sm text-ink-muted" data-testid="mfa-signin-again">For security, please sign out and sign in again to manage 2-step verification.</p>;
  }

  const run = async (fn) => {
    setBusy(true); setErr("");
    try { await fn(); } catch (e) { setErr(firebaseErrorMessage(e)); } finally { setBusy(false); }
  };

  const start = () => run(async () => {
    const session = await multiFactor(fu).getSession();
    setSecret(await TotpMultiFactorGenerator.generateSecret(session));
  });

  const confirm = () => run(async () => {
    await multiFactor(fu).enroll(TotpMultiFactorGenerator.assertionForEnrollment(secret, code.trim()), "Authenticator app");
    setFactors(multiFactor(fu).enrolledFactors);
    setSecret(null); setCode("");
    toast.success("2-step verification is on");
  });

  const remove = (f) => run(async () => {
    await multiFactor(fu).unenroll(f);
    setFactors(multiFactor(fu).enrolledFactors);
    toast.success("2-step verification turned off");
  });

  return (
    <div className="p-6 space-y-4" data-testid="mfa-settings">
      <p className="text-sm text-ink-muted">Add a second step at sign-in using an authenticator app (Google Authenticator, Microsoft Authenticator, 1Password).</p>
      {factors.length > 0 && !secret && factors.map((f) => (
        <div key={f.uid} className="flex items-center justify-between p-3 border border-good/30 bg-good/5 rounded-sm" data-testid="mfa-enrolled">
          <div className="flex items-center gap-2 text-sm"><ShieldCheck className="w-4 h-4 text-good" /> {f.displayName || "Authenticator app"} is active</div>
          <button onClick={() => remove(f)} disabled={busy} data-testid="mfa-disable-btn" className="text-[13px] text-warn hover:underline flex items-center gap-1"><ShieldOff className="w-3.5 h-3.5" /> Turn off</button>
        </div>
      ))}
      {factors.length === 0 && !secret && (
        <button onClick={start} disabled={busy} data-testid="mfa-enable-btn" className="px-5 py-2 text-sm bg-brand hover:bg-brand-hover disabled:opacity-60 text-white font-medium rounded-sm flex items-center gap-1.5">
          <ShieldCheck className="w-4 h-4" /> {busy ? "Preparing…" : "Turn on 2-step verification"}
        </button>
      )}
      {secret && (
        <div className="space-y-3" data-testid="mfa-enroll-step">
          <div className="flex gap-4 items-center">
            <QRCodeSVG value={secret.generateQrCodeUrl(fu.email, "TWIN")} size={120} data-testid="mfa-qr" />
            <div className="text-[12px] text-ink-muted">Scan with your authenticator app, or enter this key:<div className="font-num text-ink break-all mt-1" data-testid="mfa-secret">{secret.secretKey}</div></div>
          </div>
          <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6-digit code" inputMode="numeric" data-testid="mfa-enroll-code-input" className="w-full px-3 py-2 border border-ink-line rounded-sm text-sm font-num tracking-[0.3em]" />
          <button onClick={confirm} disabled={busy || code.length !== 6} data-testid="mfa-enroll-confirm-btn" className="px-5 py-2 text-sm bg-brand hover:bg-brand-hover disabled:opacity-60 text-white font-medium rounded-sm">{busy ? "Verifying…" : "Confirm & turn on"}</button>
        </div>
      )}
      {err && <div className="text-sm text-warn bg-warn-soft border border-warn/30 px-3 py-2 rounded-sm" data-testid="mfa-settings-error">{err}</div>}
    </div>
  );
}
